import React, { type PropsWithChildren, useRef } from 'react';

import { SpotlightManager } from '@atlaskit/onboarding';
import { PopoverProvider } from '@atlaskit/spotlight';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import { AppContextProvider, type AppContextProviderProps } from '../../../common/ui/app-context';
import { useFeatures } from '../../../controllers/store';
import {
	type BootstrapContainerProps,
	BootstrapStoreProvider,
} from '../../../controllers/store/bootstrap/bootstrap-store-provider';
import { DropdownStoreContainer } from '../../../controllers/store/dropdown';
import { SearchStoreProvider } from '../../../controllers/store/search/search-store-provider';

import Intercom from './intercom';
import { RegisterHooksWithNewFilters } from './register-hooks';

type ShellProps = PropsWithChildren<BootstrapContainerProps & AppContextProviderProps>;

const ConditionalSpotlightManager = ({ children }: PropsWithChildren) => {
	const [features] = useFeatures();

	if (features?.engagement?.enabled) {
		return expValEquals('company_hub_deprecate_atlaskit_onboarding', 'isEnabled', true) ? (
			<PopoverProvider>{children}</PopoverProvider>
		) : (
			<SpotlightManager blanketIsTinted={false}>{children}</SpotlightManager>
		);
	}

	return <>{children}</>;
};

export const Shell = ({ children, ...props }: ShellProps): React.JSX.Element => {
	const { mapUserIdToProfileUrl, linkComponent, generateSearchUrl, queryParams, onNavigate } =
		props;

	// Ensure callback is stable by using the initial value only
	const mapUserIdToProfileUrlRef = useRef(mapUserIdToProfileUrl);

	return (
		<DropdownStoreContainer>
			<BootstrapStoreProvider {...props}>
				<SearchStoreProvider>
					<Intercom>
						<ConditionalSpotlightManager>
							<AppContextProvider
								linkComponent={linkComponent}
								mapUserIdToProfileUrl={mapUserIdToProfileUrlRef.current}
								generateSearchUrl={generateSearchUrl}
								queryParams={queryParams}
								onNavigate={onNavigate}
							>
								<RegisterHooksWithNewFilters>{children}</RegisterHooksWithNewFilters>
							</AppContextProvider>
						</ConditionalSpotlightManager>
					</Intercom>
				</SearchStoreProvider>
			</BootstrapStoreProvider>
		</DropdownStoreContainer>
	);
};
